import { type ReactNode } from 'react'
import styles from './InputCheckbox.module.scss'

export type InputCheckboxProps = {
    id?: string
    /** Generic label. Ignored when `displayName` or `supportingText` is set. */
    label?: ReactNode
    /** Bold opening sentence from the consent CMS. */
    displayName?: string
    /** Remaining copy shown in normal weight after `displayName`. */
    supportingText?: string
    error?: string
    /** null = unanswered/untouched, true/false = the user's actual answer. */
    value: boolean | null
    onChange: (value: boolean) => void
}

const consentLabel = (displayName?: string, supportingText?: string): ReactNode => {
    if (!displayName && !supportingText) return null

    return (
        <>
            {displayName ? <span className={styles.displayName}>{displayName}</span> : null}
            {displayName && supportingText ? ' ' : null}
            {supportingText ? <span className={styles.supportingText}>{supportingText}</span> : null}
        </>
    )
}

/**
 * Consent checkbox — design cloned from @sentinel-legal/forms InputCheckbox (form-ui).
 */
export const InputCheckbox = ({
    id,
    label,
    displayName,
    supportingText,
    error,
    value,
    onChange,
}: InputCheckboxProps) => {
    const checked = value === true
    const resolvedLabel = consentLabel(displayName, supportingText) ?? label

    return (
        <div className={styles.root}>
            <label htmlFor={id} className={`${styles.label} ${checked ? styles.labelChecked : ''}`}>
                <span className={styles.control}>
                    <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => onChange(event.target.checked)}
                        className={`${styles.input} ${checked ? styles.inputChecked : ''}`}
                    />
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                        className={`${styles.icon} ${checked ? styles.iconChecked : ''}`}
                    >
                        <path
                            d="M5 13l4 4L19 7"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                {resolvedLabel ? (
                    <span className={`${styles.copy} ${checked ? styles.copyChecked : ''}`}>{resolvedLabel}</span>
                ) : null}
            </label>
            {error ? <p className={styles.error}>{error}</p> : null}
        </div>
    )
}
